import React, { useState, useRef } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus, Download, Trash2, ExternalLink } from 'lucide-react';

export default function OrderDownloadsPanel({ orderId, customerEmail }) {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [label, setLabel] = useState('');
  const [fileUrl, setFileUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  const { data: downloads = [], isLoading } = useQuery({
    queryKey: ['order-downloads', orderId],
    queryFn: () => base44.entities.OrderDownload.filter({ order_id: orderId }, '-created_date'),
  });

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const { file_url } = await base44.integrations.Core.UploadFile({ file });
    setFileUrl(file_url);
    setUploading(false);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!label.trim() || !fileUrl) return;
    setSaving(true);
    await base44.functions.invoke('adminAddDownload', { orderId, label, fileUrl });
    await queryClient.invalidateQueries({ queryKey: ['order-downloads', orderId] });
    setAdding(false);
    setLabel('');
    setFileUrl('');
    setSaving(false);
  };

  const handleDelete = async (downloadId) => {
    await base44.entities.OrderDownload.delete(downloadId);
    await queryClient.invalidateQueries({ queryKey: ['order-downloads', orderId] });
  };

  return (
    <div className="space-y-3">
      {isLoading ? (
        <div className="flex justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-primary" /></div>
      ) : downloads.length === 0 && !adding ? (
        <p className="text-white/20 text-xs text-center py-2">No downloads attached</p>
      ) : (
        <div className="space-y-2">
          {downloads.map(d => (
            <div key={d.id} className="flex items-center gap-2 bg-[#111115] border border-white/5 px-3 py-2">
              <Download className="w-3.5 h-3.5 text-primary shrink-0" />
              <span className="text-white text-xs flex-1 truncate">{d.label}</span>
              <a href={d.file_url} target="_blank" rel="noopener noreferrer" className="text-primary/60 hover:text-primary transition-colors p-1">
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
              <button onClick={() => handleDelete(d.id)} className="text-red-400/40 hover:text-red-400 transition-colors p-1">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {adding ? (
        <form onSubmit={handleSave} className="space-y-2 border border-white/10 p-3">
          <div>
            <p className="text-white/30 text-[9px] font-bold tracking-widest mb-1">LABEL</p>
            <input value={label} onChange={e => setLabel(e.target.value)} placeholder="e.g. Setup Guide PDF"
              className="w-full bg-[#111115] border border-white/10 text-white text-xs h-8 px-3 focus:outline-none focus:border-primary/60 placeholder:text-white/20" />
          </div>
          <div>
            <p className="text-white/30 text-[9px] font-bold tracking-widest mb-1">FILE</p>
            <input ref={fileRef} type="file" onChange={handleFileUpload} className="hidden" />
            {fileUrl ? (
              <p className="text-green-400 text-xs truncate">✓ File uploaded</p>
            ) : (
              <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
                className="flex items-center gap-1.5 border border-white/10 hover:border-primary/40 text-white/40 hover:text-white text-[10px] font-bold tracking-widest px-3 py-1.5 transition-colors disabled:opacity-40">
                {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                {uploading ? 'UPLOADING…' : 'UPLOAD FILE'}
              </button>
            )}
          </div>
          <div className="flex gap-2 pt-1">
            <button type="submit" disabled={saving || !label.trim() || !fileUrl}
              className="flex items-center gap-1.5 bg-primary hover:bg-primary/90 text-white text-[10px] font-bold tracking-widest px-3 py-1.5 transition-colors disabled:opacity-40">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />} SAVE
            </button>
            <button type="button" onClick={() => { setAdding(false); setLabel(''); setFileUrl(''); }}
              className="text-white/30 text-[10px] font-bold px-3 py-1.5 hover:text-white transition-colors">
              CANCEL
            </button>
          </div>
        </form>
      ) : (
        <button onClick={() => setAdding(true)}
          className="flex items-center gap-1.5 text-white/40 hover:text-white text-[10px] font-bold tracking-widest transition-colors">
          <Plus className="w-3.5 h-3.5" /> ATTACH DOWNLOAD
        </button>
      )}
    </div>
  );
}